import React, { useState, useEffect } from 'react';
import { fsGetCol } from '../../lib/firebase';
import { toLocalDateStr, fmtDayDate } from '../../lib/datetime';
import { SESSION_STATUS } from '../../config/appConfig';
import SessionCancelSheet from '../../components/SessionCancelSheet';

// ─── CAREGIVER SESSION TAB ────────────────────────────────────────────────────
// "My Sessions" for a token link bound to a caregiver record: upcoming
// sessions where sitterId === tokenDoc.caregiverId. Declined sessions stay
// visible (styled red) rather than being filtered out — the point is for a
// caregiver to see their own decline reflected back, not for it to vanish.

function sessionTitle(session, children) {
  if (session.activityType === 'dogs_only') return 'Dog-sitting';
  const names = (session.kidIds || [])
    .map(id => children.find(c => c.id === id)?.firstName)
    .filter(Boolean);
  if (!names.length) return 'Watching the kids';
  return `Watching ${names.length > 1 ? `${names.slice(0, -1).join(', ')} & ${names.slice(-1)}` : names[0]}`;
}

export default function SessionTab({ familyData, caregiverId }) {
  const [sessions, setSessions] = useState(null); // null = loading
  const [cancelTarget, setCancelTarget] = useState(null); // session being cancelled, or null
  const children = familyData?.children || [];

  useEffect(() => {
    if (!familyData?.id || !caregiverId) { setSessions([]); return; }
    const today = toLocalDateStr(new Date());
    fsGetCol(`families/${familyData.id}/sessions`, [
      { field: 'sitterId', op: '==', value: caregiverId },
    ])
      .then(all => setSessions(
        (all || [])
          .filter(s => s.date >= today)
          .sort((a, b) => a.date.localeCompare(b.date))
          .slice(0, 10)
      ))
      .catch(() => setSessions([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyData?.id, caregiverId]);

  function handleDeclined(sessionId) {
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, status: SESSION_STATUS.DECLINED } : s));
    setCancelTarget(null);
  }

  if (sessions === null) return (
    <div style={{ padding: 32 }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
  );

  if (!caregiverId) return (
    <p style={{ padding: '24px 0', fontSize: 14, color: 'var(--sage)' }}>
      This link isn't tied to a caregiver profile yet — ask the family to relink it.
    </p>
  );

  if (!sessions.length) return (
    <p style={{ padding: '24px 0', fontSize: 14, color: 'var(--sage)' }}>
      No upcoming sessions scheduled.
    </p>
  );

  return (
    <div className="schedule-list" style={{ paddingTop: 12 }}>
      {sessions.map((s, i) => {
        const isDeclined = s.status === SESSION_STATUS.DECLINED;
        return (
          <div key={s.id || i} className={`ui-card${isDeclined ? ' ui-card--declined' : ''}`} style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: 'var(--forest-dark)' }}>{fmtDayDate(s.date)}</span>
              <span style={{ fontSize: 13, color: 'var(--forest)' }}>{s.startTime}–{s.endTime}</span>
              {isDeclined && <span className="badge badge--red">Declined</span>}
            </div>
            <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--forest-dark)', margin: '4px 0 0' }}>
              {sessionTitle(s, children)}
            </p>
            {s.note && <p style={{ fontSize: 13, color: 'var(--forest)', margin: '6px 0 0' }}>{s.note}</p>}
            {!isDeclined && (
              <button className="btn btn--ghost btn--small session-cancel-btn" style={{ marginTop: 8 }}
                onClick={() => setCancelTarget(s)}>
                Cancel
              </button>
            )}
          </div>
        );
      })}

      {cancelTarget && (
        <SessionCancelSheet
          familyData={familyData}
          session={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onDone={() => handleDeclined(cancelTarget.id)}
        />
      )}
    </div>
  );
}
